/**
 * ComplianceSidebar Component
 * 
 * Displays FAR 117 compliance status and violations in sticky sidebar
 */

import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const ComplianceSidebar = ({ far117Compliant, far117Violations, dutyPeriods, totals }) => {
  const isCompliant = far117Compliant;
  const violations = far117Violations || [];
  
  // Convert minutes to HH:MM
  const formatMinutes = (minutes) => {
    if (!minutes) return '00:00';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };
  
  // Get severity color
  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'text-red-700 bg-red-50 border-red-300';
      case 'warning':
        return 'text-yellow-700 bg-yellow-50 border-yellow-300';
      default:
        return 'text-gray-700 bg-gray-50 border-gray-300';
    }
  };
  
  return (
    <div className="sticky top-4 space-y-4">
      {/* Compliance Status Card */}
      <div className={`
        rounded-lg border-2 p-4
        ${isCompliant 
          ? 'bg-green-50 border-green-300' 
          : 'bg-red-50 border-red-300'
        }
      `}>
        <div className="flex items-center gap-3 mb-3">
          {isCompliant ? (
            <>
              <span className="text-4xl flex-shrink-0">✅</span>
              <div>
                <h3 className="font-bold text-green-900">Compliant</h3>
                <p className="text-sm text-green-700">FAR 117 Rules</p>
              </div>
            </>
          ) : (
            <>
              <span className="text-4xl flex-shrink-0">⚠️</span>
              <div>
                <h3 className="font-bold text-red-900">Violations Detected</h3>
                <p className="text-sm text-red-700">FAR 117 Rules</p>
              </div>
            </>
          )}
        </div>
        
        {isCompliant ? (
          <p className="text-sm text-green-700">
            This rotation meets all FAR 117 flight and duty time limitations.
          </p>
        ) : (
          <p className="text-sm text-red-700">
            {violations.length} violation{violations.length !== 1 ? 's' : ''} found. 
            See details below.
          </p>
        )}
      </div>
      
      {/* Violations List */}
      {violations.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
            Violations
          </h4>
          
          <div className="space-y-3">
            {violations.map((violation, idx) => (
              <div 
                key={idx}
                className={`border-2 rounded p-3 text-sm ${getSeverityColor(violation.severity)}`}
              >
                <p className="font-semibold mb-1">
                  {violation.violation_type?.replace(/_/g, ' ')}
                </p>
                <p className="mb-2">{violation.description}</p>
                
                {violation.duty_period && (
                  <p className="text-xs opacity-75">
                    Duty Period: {violation.duty_period}
                  </p>
                )}
                
                {violation.limit && (
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="opacity-75">Actual:</span>
                      <span className="font-bold ml-1">
                        {formatMinutes(violation.actual_value * 60)}
                      </span>
                    </div>
                    <div>
                      <span className="opacity-75">Limit:</span>
                      <span className="font-bold ml-1">
                        {formatMinutes(violation.limit * 60)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Rolling Limits Summary */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
          <span className="text-xl text-blue-600">🛡️</span>
          Key Limits
        </h4>
        
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Total Duty Time</span>
            <span className="font-bold text-gray-900">
              {formatMinutes(totals?.total_duty_minutes)}
            </span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Total Block Time</span>
            <span className="font-bold text-gray-900">
              {formatMinutes(totals?.total_block_minutes)}
            </span>
          </div>
          
          {dutyPeriods && (
            <div className="flex justify-between">
              <span className="text-gray-600">Duty Periods</span>
              <span className="font-bold text-gray-900">
                {dutyPeriods.length}
              </span>
            </div>
          )}
          
          <div className="pt-3 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              FAR 117 sets limits on flight time, duty time, and rest periods to manage fatigue.
            </p>
          </div>
        </div>
      </div>
      
      {/* Legal Disclaimer */}
      <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 text-xs text-yellow-800">
        <p className="font-semibold mb-1">⚠️ Disclaimer</p>
        <p>
          FAR 117 predictions are estimates, not guarantees. Consult with scheduling 
          for official compliance verification.
        </p>
      </div>
    </div>
  );
};

export default ComplianceSidebar;
